// src/pages/Order/Purchase Order/Purchase Order Entry/components/PurchaseOrderTable.jsx
import React, { useState } from "react";
import Big from "big.js";
import { Table, Button, Checkbox, Popover, Select, Input } from "antd";
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InputNumberField from "../../../../../components/Input/InputNumber";
import SearchListAutoComplete from "../../../../../components/SearchListAutoComplete";
import {
  addOrderRow,
  deleteOrderRow,
  updateOrderRow,
  calculateTotals,
  setFooterValues,
} from "../purchaseOrderSlice";
import {
  selectItems,
  selectWarehouses,
} from "../../../../../store/commonSelectors/lookupsSelectors";
import SharedFooter from "../../../../../components/DataTableFooter/SharedFooter";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import { getCurrencySymbol } from "../../../../../lib/currency";
import { useNavigate } from "react-router-dom";
import ProductQuickDrawer from "../../../../Product_manage/components/drawers/ProductQuickDrawer";
import { fetchItems } from "../../../../../store/commonThunks/lookups";
import { fetchPrediction } from "../purchaseOrderThunk";
import { getItemDisplayName } from "../../../../../utils";
import "./purchaseOrder.css"

const PurchaseOrderTable = ({editId, institution }) => {
  const dispatch = useDispatch();
  const items = useSelector(selectItems);
  const warehouses = useSelector(selectWarehouses);
  const footerValues = useSelector((s) => s.purchaseOrder.footerValues);
  const itemCart = useSelector((s) => s.purchaseOrder.itemCart);
  const taxMode = useSelector((s) => s.purchaseOrder.taxMode);
  const predictionMap = useSelector((s) => s.purchaseOrder.predictionMap);
  const authInfo = useSelector(selectAuthInfo);
  const currency = authInfo?.currency || "AED";
  const symbol = getCurrencySymbol(currency);
  const conversionRate = useSelector((s) => s.purchaseOrder.conversionRate);
  const conversionCurrency = useSelector(
    (s) => s.purchaseOrder.conversionCurrency
  );
  const [productDrawer, setProductDrawer] = useState({
    open: false,
    rowKey: null,
  });

  const navigate = useNavigate();

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;
  // const handleUpdate = (rowKey, field, value) => {
  //   dispatch(updateOrderRow({ rowKey, updates: { [field]: value } }));
  //   dispatch(calculateTotals({ authInfo }));
  // };

  const handleUpdate = (rowKey, field, value) => {
    const row = itemCart.find((r) => r.key === rowKey);
    const updates = { [field]: value };

    if (["quantity", "rate", "discount", "discountAmt", "discountMode", "tax_per", "bonus"].includes(field)) {
      const qty = Number(field === "quantity" ? value : row.quantity) || 0;
      const rate = Number(field === "rate" ? value : row.rate) || 0;
      const tax_per = Number(field === "tax_per" ? value : row.tax_per) || 0;
      const discount = Number(field === "discount" ? value : row.discount) || 0;
      const discountMode = (field === "discountMode" ? value : row.discountMode) === "amount" ? "amount" : "percent";
      const discountAmtInput = Number(field === "discountAmt" ? value : row.discountAmt) || 0;

      let line = new Big(qty).times(rate);
      if (taxMode === "on_item") {
        let discountAmt;
        if (discountMode === "amount") {
          const raw = new Big(discountAmtInput);
          discountAmt = raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
        } else {
          discountAmt = line.times(discount).div(100) || 0;
        }
        line = line.minus(discountAmt);
        const taxAmt = line.times(tax_per).div(100) || 0;
        line = line.plus(taxAmt);
      }

      const finalLine = conversionRate
      ? line.times(conversionRate)
      : line;

      updates.line_total = finalLine.toFixed(2);
    }

    dispatch(updateOrderRow({ rowKey, updates }));
    dispatch(calculateTotals({ authInfo }));
  };

  const handleAddRow = () => {
    const newRow = {
      key: Date.now(),
      item_id: null,
      product: "",
      quantity: null,
      rate: null,
      tax_per: null,
      discount: null,
      discountAmt: null,
      discountMode: "percent",
      bonus: false,
      warehouse_id: null,
      item_description: "",
      showDescription: false,
    };
    dispatch(addOrderRow(newRow));
  };

  const handleFooterChange = (value, field) => {
    let updatedFooter = { ...footerValues };
    if (field === "payCart") {
      const newPaidAmount = value.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );
      updatedFooter = {
        ...updatedFooter,
        payCart: value,
        paid_amount: newPaidAmount,
        paidAmount: newPaidAmount,
        dueAmount: (
          parseFloat(updatedFooter.grandTotal || 0) - newPaidAmount
        ).toFixed(2),
      };
    } else if (field === "paidAmount") {
      updatedFooter.paidAmount = Number(value) || 0;
      updatedFooter.dueAmount = (
        parseFloat(updatedFooter.grandTotal || 0) - updatedFooter.paidAmount
      ).toFixed(2);
    } else {
      updatedFooter[field] = value;
    }

    dispatch(setFooterValues(updatedFooter));
    dispatch(calculateTotals({ authInfo }));
  };

  const baseColumns = [
    {
      title: "Bonus",
      dataIndex: "bonus",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={record.bonus}
          onChange={(e) => {
            handleUpdate(record.key, "bonus", e.target.checked);
            handleUpdate(record.key, "product", null);
            // 🔥 FULL CLEAR            
            dispatch(
              updateOrderRow({
                rowKey: record.key,
                updates: {
                  qty: null,
                  discount: 0,
                  tax_per: 0,
                  unit_symbol: "",
                  units: [],
                  conversion: 1,
                  per_conversion: 1,
                  quantity: null,
                  warehouse_id: null,
                },
              })
            );
            dispatch(calculateTotals({ authInfo }));


          }}
        />
      ),
    },
    {
      title: "Item",
      dataIndex: "product",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <SearchListAutoComplete
          entityType="item"
          placeholder="Item"
          showAddNew={true}
          style={{ width: "100%" }}
          optionsList={items
            .filter((i) =>
                  i.is_service !== 'yes' &&  
              !itemCart.some((row) => {
                if (row.key === record.key) return false;

                const sameProduct = row.item_id === i.item_id;
                const sameBonus = Boolean(row.bonus) === Boolean(record.bonus);

                return sameProduct && sameBonus;
              })
            )
            .map((i) => ({
              sale_id: i.item_id,
              display_text: getItemDisplayName(i),
              raw: i,
            }))
          }
          value={
            record.item_id
              ? {
                  // `product` is also what gets saved as this row's
                  // item_name, so it stays the plain name -- the fuller
                  // "name - code - barcode" label only lives in
                  // product_display, used for showing/picking in this field.
                  value: record.product_display || record.product,
                  label: record.product_display || record.product,
                  display_text: record.product_display || record.product,
                  raw: record,
                }
              : null
          }
          onSelect={async (obj) => {
            // 🔥 FULL CLEAR
            if (!obj) {
              dispatch(
                updateOrderRow({
                  rowKey: record.key,
                  updates: {
                    item_id: null,
                    product: "",
                    product_display: "",
                    qty: null,
                    rate: null,
                    discount: 0,
                    tax_per: 0,
                    unit_symbol: "",
                    units: [],
                    conversion: 1,
                    per_conversion: 1,
                    quantity: null,
                    bonus: false,
                    warehouse_id: null,
                    item_description: "",
                    showDescription: false,
                  },
                })
              );
              dispatch(calculateTotals({ authInfo }));
              return;
            }
            if (obj === "add_new") {
              setProductDrawer({
                open: true,
                rowKey: record.key,
              });
              return;
            }
            const it =
              items.find((i) => i.item_id === obj?.sale_id) || obj?.raw;
            let suggestedQty = record.quantity;

            let prediction = predictionMap[it.item_id];

            try {
              if (!prediction) {
                prediction = await dispatch(fetchPrediction(it.item_id)).unwrap();
              }

              if (!record.quantity || record.quantity === 1) {
                suggestedQty = prediction.final_suggestion;
              }
            } catch (err) {
              console.error("Prediction failed", err);
            }

            dispatch(
              updateOrderRow({
                rowKey: record.key,
                updates: {
                  item_id: it.item_id,
                  product: it.item_name,
                  product_display: getItemDisplayName(it),
                  quantity: editId ? record.quantity : suggestedQty,
                  rate: it.purchase_rate ?? 0,
                  tax_per: it.tax_per ?? 0,
                  item_description: String(it.narration ?? "").trim(),
                  showDescription: !!String(it.narration ?? "").trim(),
                },
              })
            );
            dispatch(calculateTotals({ authInfo }));
          }}
        />
        {record.item_id && (
          <Button
            type="link"
            size="small"
            style={{ padding: 0, alignSelf: "flex-start" }}
            onClick={() =>
              handleUpdate(record.key, "showDescription", !record.showDescription)
            }
          >
            {record.showDescription ? "Hide Description" : "Add Description"}
          </Button>
        )}
        {record.showDescription && (
          <Input.TextArea
            rows={2}
            placeholder="Add item description"
            value={record.item_description}
            onChange={(e) =>
              handleUpdate(record.key, "item_description", e.target.value)
            }
          />
        )}
        </div>
      ),
    },
    // {
    //   title: "Warehouse",
    //   dataIndex: "warehouse_id",
    //   render: (_, record) => (
    //     <SearchListAutoComplete
    //       entityType="warehouse"
    //       placeholder="Select Warehouse"
    //       style={{ width: "100%" }}
    //       optionsList={warehouses.map((w) => ({
    //         sale_id: w.warehouse_id,
    //         display_text: w.warehouse_name,
    //         raw: w,
    //       }))}
    //       value={
    //         record.warehouse_id
    //           ? {
    //               value: record.warehouse_id,
    //               label:
    //                 warehouses.find((w) => w.warehouse_id === record.warehouse_id)
    //                   ?.warehouse_name || "",
    //             }
    //           : undefined
    //       }
    //       onSelect={(obj) =>
    //         handleUpdate(record.key, "warehouse_id", obj?.sale_id)
    //       }
    //     />
    //   ),
    // },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 130,
      render: (_, record) => {
        const p = predictionMap[record.item_id];

        const hasSuggestion = p && p.final_suggestion > 0;

        const content = p && (
          <div style={{ fontSize: 12, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>📊 Sales</div>
            <div>7 Days: <b>{p.last_7_days_qty}</b></div>
            <div>30 Days: <b>{p.last_30_days_qty}</b></div>

            <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>📦 Stock</div>
            <div>Current: <b>{p.current_stock}</b></div>

            <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>💡 Suggestion</div>

            {hasSuggestion ? (
              <div style={{ color: "#fa8c16", fontWeight: 600 }}>
                Buy {p.final_suggestion} units
              </div>
            ) : (
              <div style={{ color: "#52c41a", fontWeight: 500 }}>
                Stock sufficient
              </div>
            )}
          </div>
        );

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            
            {/* Quantity Input */}
            <InputNumberField
              className={
                hasSuggestion
                  ? record.userEdited
                    ? "qty-suggest user-edited"
                    : "qty-suggest"
                  : "qty-ok"
              }
              autoFilled= {true}
              min={0}
              precision={1}
              step={0.5}
              value={record.quantity}
              onClick={() => {
                if (record.quantity !== null) {
                  handleUpdate(record.key, "quantity", null);
                }
              }}
              onChange={(v) => {
                handleUpdate(record.key, "quantity", v);
                handleUpdate(record.key, "userEdited", true);
              }}
              style={{
                background: hasSuggestion ? "#fffbe6" : "#f6ffed",
              }}
            />

            {/* Info Icon */}
            {p && (
              <Popover
                content={content}
                placement="top"
                trigger={["hover", "focus"]}
              >
                <InfoCircleOutlined
                  style={{
                    fontSize: 14,
                    color: hasSuggestion ? "#faad14" : "#52c41a",
                    cursor: "pointer",
                  }}
                />
              </Popover>
            )}
          </div>
        );
      },
    },
    {
      title: "Rate",
      dataIndex: "rate",
      width: 120,
      render: (_, record) => (
        <InputNumberField
          min={0}
          step={0.01}
          precision={2}
          value={
            record.bonus
              ? 0
              : conversionRate
              ? Number(record.rate || 0) * Number(conversionRate)
              : record.rate
          }
          placeholder="0.00"
          onClick={() => {
            if (!record.bonus && Number(record.rate) >= 0) {
              handleUpdate(record.key, "rate", null);
            }
          }}
          onChange={(v) => handleUpdate(record.key, "rate", v)}
          // value={
          //   record.bonus
          //     ? 0
          //     : parseFloat(
          //         conversionRate ? record?.rate * conversionRate : record?.rate
          //       )?.toFixed(2)
          // }
          // onChange={(v) => handleUpdate(record.key, "rate", v)}
        />
      ),
    },
  ];

  const individualColumns = [
    {
      title: "Discount",
      dataIndex: "discount",
      width: 160,
      render: (_, record) => {
        const mode = record.discountMode === "amount" ? "amount" : "percent";
        const lineTotal = new Big(Number(record.quantity) || 0).times(Number(record.rate) || 0);
        // One merged compound field (number input + unit-select addon)
        // instead of a separate Select stacked above the number input.
        const modeSelect = (
          <Select
            size="small"
            style={{ width: 65 }}
            disabled={record.bonus}
            value={mode}
            options={[
              { label: "%", value: "percent" },
              { label: symbol, value: "amount" },
            ]}
            onChange={(v) => handleUpdate(record.key, "discountMode", v)}
          />
        );
        return mode === "amount" ? (
          <InputNumberField
            min={0}
            max={Number(lineTotal)}
            precision={2}
            disabled={record.bonus}
            value={record.discountAmt}
            onChange={(v) => handleUpdate(record.key, "discountAmt", v)}
            addonAfter={modeSelect}
          />
        ) : (
          <InputNumberField
            min={0}
            max={100}
            precision={2}
            disabled={record.bonus}
            value={record.discount}
            onClick={() => {
              if (Number(record.discount) >= 0) {
                handleUpdate(record.key, "discount", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "discount", v)}
            addonAfter={modeSelect}
          />
        );
      },
    },
  ];

  const gstColumn = showTax
    ? [
      {
        title: `${taxLabel} %`,
        dataIndex: "tax_per",
        width: 120,
        render: (_, record) => (
          <InputNumberField
            min={0}
            disabled={record.bonus}
            value={record.tax_per}
            placeholder="0"
            onClick={() => {
              if (Number(record.tax_per) >= 0) {
                handleUpdate(record.key, "tax_per", null);
              }
            }}
            onChange={(v) => handleUpdate(record.key, "tax_per", v)}
          />
        ),
      },
    ]
    : [];

  const amountColumn = [
    {
      title: "Line Total",
      render: (_, r) => {
        if (r.bonus) return "0.00";

        const qty = Number(r.quantity) || 0;
        const rate = Number(r.rate) || 0;
        let line = new Big(qty).times(rate);

        if (taxMode === "on_item") {
          const discountMode = r.discountMode === "amount" ? "amount" : "percent";
          let discountAmt;
          if (discountMode === "amount") {
            const raw = new Big(r.discountAmt || 0);
            discountAmt = raw.gt(line) ? line : raw.lt(0) ? new Big(0) : raw;
          } else {
            discountAmt = line.times(Number(r.discount || 0)).div(100);
          }
          line = line.minus(discountAmt);

          const taxAmt = showTax ? line.times(Number(r.tax_per || 0)).div(100) : new Big(0);
          line = line.plus(taxAmt);
        }
        const convertedLine = conversionRate
        ? line.times(conversionRate).toFixed(2)
        : line.toFixed(2);
        return convertedLine;
      },
    },
    {
      title: "",
      render: (_, r) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => dispatch(deleteOrderRow(r.key))}
          disabled={itemCart.length === 1}
        />
      ),
    },
  ];

  const columns =
    taxMode === "on_item"
      ? [...baseColumns, ...individualColumns, ...gstColumn, ...amountColumn]
      : [...baseColumns, ...amountColumn];

  return (
    <div>
      <Button
        type="dashed"
        onClick={handleAddRow}
        icon={<PlusOutlined />}
        style={{ marginBottom: 8 }}
      >
        Add Row
      </Button>
      <div style={{ width: "100%", maxWidth: "1300px", overflowX: "auto" }}>
        <Table
          dataSource={itemCart}
          columns={columns}
          pagination={false}
          rowKey="key"
          scroll={{ x: "max-content" }}
          footer={() => (
            <SharedFooter
              footerValues={footerValues}
              dataSource={itemCart}
              onFooterChange={handleFooterChange}
              pageType="purchaseOrder"
              taxMode={taxMode}
              conversionRate={conversionRate}
              conversionCurrency={conversionCurrency}
            />
          )}
        />
      </div>

      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;

          const productValue = {
            ...product,
            item_id: product.item_id,
            item_name: product.item_name,
            display_text: product.item_name,
            tax_per: product.tax_per,
            purchase_rate: product.purchase_rate,
            raw: product,
          };

          // ✅ update table row product
          dispatch(
            updateOrderRow({
              rowKey: productDrawer.rowKey,
              updates: {
                item_id: productValue.item_id,
                product: productValue.item_name,
                product_display: getItemDisplayName(productValue),
                rate: Number(productValue.purchase_rate || 0),
                tax_per: productValue.tax_per,
                quantity: null,
                bonus: false,
                unit_id: productValue.unit_id || 0,
                unit_name: productValue.unit_name || "",
                unit_symbol: productValue.unit_symbol || "",
                units: productValue.units || [],
                item_description: String(productValue.narration ?? "").trim(),
                showDescription: !!String(productValue.narration ?? "").trim(),
              },
            })
          );

          dispatch(calculateTotals({ authInfo }));

          // 2️⃣ 🔥 Re-trigger product search so dropdown list refreshes
          dispatch(fetchItems());

          // ✅ CLOSE DRAWER FIRST
          setProductDrawer({ open: false, rowKey: null });
        }}
      />
    </div>
  );
};

export default PurchaseOrderTable;
